// The entry point form (spec 11 6.15.10, mock n05c's layout): name, tier and region, add-ons with their costs, domains,
// routes with their origin and origin access, the dry run's diagnostics at their fields and its cost line, and Save with
// the whole `settings.entryPoints` array and If-Match (a 412 merges this entry point into the newer array).
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { isApiError, type Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { queryKeys, useAttachments } from '../../api/queries';
import type {
  Diagnostic,
  EdgeKind,
  EntryPointAddOnSettings,
  EntryPointSettings,
  Network,
  NetworkValidation,
  OriginAccessMode,
  PatchNetworkRequest,
} from '../../api/types';
import { useToast } from '../../components/toast';
import { Dialog, FieldMessages, IconButton } from '../../components/ui';
import { strings } from '../../strings';
import { ACCESS_ORDER, ACCESS_STRENGTH, addOnEnabled, offeredModes, preselectedMode } from './helpers';

const e = strings.networks.entryPoints;
const ID_PATTERN = /^[a-z][a-z0-9-]{0,30}$/;
const HOST_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const PATH_PATTERN = /^\/[^*\s]*(\/\*)?$|^\/\*$/;
const PLATFORM_OF_TYPE: Record<string, string> = {
  'compute.webapp': 'app-service',
  'compute.function': 'functions-flex',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10" → "Oct 2026". */
function asOfWords(asOf: string): string {
  const [year, month] = asOf.split('-');
  return month ? `${MONTHS[Number(month) - 1] ?? month} ${year}` : asOf;
}

/** The add-ons as the form keeps them: every add-on of the kind explicit; paid ones never on by themselves (D13). */
function initialAddOns(kind: EdgeKind, ep: EntryPointSettings | null, tier: string | null) {
  const addOns: Record<string, EntryPointAddOnSettings> = {};
  for (const a of kind.addOns) {
    const saved = ep?.addOns?.[a.id];
    const tierOk = !a.requiresTier || a.requiresTier === tier;
    addOns[a.id] = saved
      ? { ...saved }
      : {
          enabled: ep ? addOnEnabled(ep, kind, a.id) : a.default && !a.cost && !a.commitment && tierOk,
        };
    if (a.id === 'waf' && !saved)
      addOns.waf = { ...addOns.waf, mode: 'prevention', rateLimitPerMinute: 2000 };
  }
  return addOns;
}

interface OriginOption {
  deploymentId: string;
  deploymentName: string;
  resourceId: string;
  platform: string;
  label: string;
  region: string;
  unavailable: string | null;
}

/** The web and function apps of the deployments attached to the network (App Gateway: in its region). */
function useOriginOptions(network: Network, kind: EdgeKind, region: string | null) {
  const api = useApi();
  const attachments = useAttachments(network.id, {}, null, { limit: 200 });
  const rows = (attachments.data?.items ?? []).filter((a) => a.state !== 'detached');
  const blueprintIds = [...new Set(rows.map((a) => a.blueprintId).filter(Boolean))];
  const blueprints = useQueries({
    queries: blueprintIds.map((id) => ({
      queryKey: queryKeys.blueprint(id),
      queryFn: () => api.getBlueprint(id),
      staleTime: 60_000,
    })),
  });
  const loading = attachments.isPending || blueprints.some((b) => b.isPending);
  const options: OriginOption[] = [];
  const seen = new Set<string>();
  for (const a of rows) {
    if (kind.scope === 'regional' && region && a.region !== region) continue;
    const doc = blueprints.find((b) => b.data?.data.id === a.blueprintId)?.data?.data.content;
    for (const r of doc?.resources ?? []) {
      const platform = PLATFORM_OF_TYPE[r.type];
      if (!platform || a.provider !== 'azure') continue;
      const key = `${a.deploymentId}/${r.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const support = kind.origins.find((o) => o.platform === platform);
      options.push({
        deploymentId: a.deploymentId,
        deploymentName: a.deploymentName,
        resourceId: r.id,
        platform,
        label: support?.label ?? platform,
        region: a.region,
        unavailable: !support
          ? e.codes.ROUTE_ORIGIN_UNKNOWN
          : support.status && support.status !== 'available'
            ? (support.reason?.text ?? support.status)
            : null,
      });
    }
  }
  return { options, loading };
}

function Section({ title, children, help }: { title: string; children: ReactNode; help?: string }) {
  return (
    <fieldset className="net-ep-section">
      <legend className="text-[13.5px] font-semibold">{title}</legend>
      {help && <p className="help mt-0 mb-2">{help}</p>}
      {children}
    </fieldset>
  );
}

export function EntryPointForm({
  network,
  kind,
  editing,
  onClose,
  onSaved,
}: {
  network: Versioned<Network>;
  kind: EdgeKind;
  editing: EntryPointSettings | null;
  onClose: () => void;
  onSaved: (message: string, id: string) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const n = network.data;
  const regions = (kind.provider && n.settings.providers[kind.provider]?.regions) || [];
  const [draft, setDraft] = useState<EntryPointSettings>(() => {
    const tier = editing?.tier ?? kind.tiers[0]?.id ?? null;
    return {
      id: editing?.id ?? '',
      label: editing?.label ?? '',
      kind: kind.kind,
      tier,
      ...(kind.scope === 'regional' ? { region: editing?.region ?? regions[0] ?? null } : {}),
      addOns: initialAddOns(kind, editing, tier),
      domains: structuredClone(editing?.domains ?? []),
      routes: structuredClone(editing?.routes ?? []).map((r) => ({
        ...r,
        paths: r.paths?.length ? r.paths : ['/*'],
      })),
    };
  });
  const [pathsText, setPathsText] = useState<string[]>(() =>
    (editing?.routes ?? []).map((r) => (r.paths?.length ? r.paths : ['/*']).join(', ')),
  );
  const origins = useOriginOptions(n, kind, draft.region ?? null);
  const index = editing
    ? n.settings.entryPoints.findIndex((x) => x.id === editing.id)
    : n.settings.entryPoints.length;
  const prefix = `/settings/entryPoints/${index}`;

  /** The draft as saved: empty label dropped, route paths from their text. */
  const saved: EntryPointSettings = useMemo(() => {
    const out: EntryPointSettings = { ...draft, label: draft.label?.trim() || null };
    if (!out.label) delete out.label;
    out.routes = (draft.routes ?? []).map((r, i) => ({
      ...r,
      paths: (pathsText[i] ?? '/*')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    }));
    return out;
  }, [draft, pathsText]);

  const listWith = (base: EntryPointSettings[], ep: EntryPointSettings) => {
    const at = base.findIndex((x) => x.id === (editing?.id ?? ep.id));
    if (editing && at >= 0) return base.map((x, i) => (i === at ? ep : x));
    return [...base, ep];
  };
  const patch: PatchNetworkRequest = { settings: { entryPoints: listWith(n.settings.entryPoints, saved) } };
  const patchKey = JSON.stringify(patch);

  // Browser checks (formats and ranges), for responsiveness; the dry run has the server's rules.
  const local: Diagnostic[] = [];
  const add = (path: string, message: string) =>
    local.push({ severity: 'error', path: `${prefix}${path}`, code: 'LOCAL', message });
  if (!ID_PATTERN.test(draft.id)) add('/id', e.idInvalid);
  else if (!editing && n.settings.entryPoints.some((x) => x.id === draft.id)) add('/id', e.idTaken(draft.id));
  const rate = draft.addOns?.waf?.rateLimitPerMinute;
  if (draft.addOns?.waf?.enabled && rate != null && rate !== 0 && (rate < 100 || rate > 100_000))
    add('/addOns/waf/rateLimitPerMinute', e.rateLimitInvalid);
  const hosts = (draft.domains ?? []).map((d) => d.hostname.trim().toLowerCase());
  (draft.domains ?? []).forEach((d, i) => {
    if (!HOST_PATTERN.test(d.hostname.trim())) add(`/domains/${i}/hostname`, e.hostInvalid);
    else if (hosts.indexOf(d.hostname.trim().toLowerCase()) !== i)
      add(`/domains/${i}/hostname`, e.hostDuplicate);
    if (d.certificate.kind === 'key-vault' && !d.certificate.secretId?.trim())
      add(`/domains/${i}/certificate/secretId`, e.secretIdRequired);
  });
  (saved.routes ?? []).forEach((r, i) => {
    if (!ID_PATTERN.test(r.id)) add(`/routes/${i}/id`, e.idInvalid);
    if (!r.origin.deploymentId) add(`/routes/${i}/origin`, e.originMissing);
    if (!r.paths?.length || r.paths.some((p) => !PATH_PATTERN.test(p)))
      add(`/routes/${i}/paths`, e.pathInvalid);
  });

  // The dry run (debounced; the last answer wins).
  const [dry, setDry] = useState<{
    key: string;
    state: 'loading' | 'done' | 'error';
    data: NetworkValidation | null;
  } | null>(null);
  const [retry, setRetry] = useState(0);
  const latest = useRef(0);
  useEffect(() => {
    const request = ++latest.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setDry((d) => ({ key: patchKey, state: 'loading', data: d?.data ?? null }));
      api
        .validateNetwork(n.id, JSON.parse(patchKey) as PatchNetworkRequest, controller.signal)
        .then((data) => {
          if (request === latest.current) setDry({ key: patchKey, state: 'done', data });
        })
        .catch(() => {
          if (request === latest.current && !controller.signal.aborted)
            setDry((d) => ({ key: patchKey, state: 'error', data: d?.data ?? null }));
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, n.id, patchKey, retry]);

  const [saveDiagnostics, setSaveDiagnostics] = useState<Diagnostic[]>([]);
  const server = dry?.state === 'done' ? (dry.data?.diagnostics ?? []) : [];
  const diagnostics = [...local, ...server.filter((d) => d.path.startsWith(prefix)), ...saveDiagnostics];
  const elsewhere = server.filter((d) => d.severity === 'error' && !d.path.startsWith(prefix));
  const errors = diagnostics.filter((d) => d.severity === 'error');
  const at = (path: string, exact = false) =>
    diagnostics.filter((d) =>
      exact
        ? d.path === `${prefix}${path}`
        : d.path === `${prefix}${path}` || d.path.startsWith(`${prefix}${path}/`),
    );
  const messages = (path: string, exact = false) => at(path, exact).map((d) => d.message);
  const hasCode = (path: string, ...codes: string[]) => at(path).some((d) => codes.includes(d.code));

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const save = async () => {
    if (errors.length) return;
    setBusy(true);
    setMessage(null);
    setSaveDiagnostics([]);
    let target = network;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const body: PatchNetworkRequest = {
            settings: { entryPoints: listWith(target.data.settings.entryPoints, saved) },
          };
          const result = await api.patchNetwork(n.id, body, target.etag);
          queryClient.setQueryData(queryKeys.network(n.id), result);
          void queryClient.invalidateQueries({ queryKey: ['network', n.id] });
          void queryClient.invalidateQueries({ queryKey: ['networks'] });
          onSaved(editing ? e.savedEdit(n.name, saved.id) : e.saved(n.name, saved.id), saved.id);
          return;
        } catch (error) {
          if (isApiError(error, 'PRECONDITION_FAILED') && attempt === 0) {
            // Merge flow: take the newer array and put this entry point into it, then send again once.
            target = await api.getNetwork(n.id);
            queryClient.setQueryData(queryKeys.network(n.id), target);
            continue;
          }
          if (isApiError(error, 'PRECONDITION_FAILED')) setMessage(e.conflict);
          else if (isApiError(error) && error.diagnostics.length) setSaveDiagnostics(error.diagnostics);
          else toast.error(error);
          return;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- updates

  const setAddOn = (id: string, next: Partial<EntryPointAddOnSettings>) =>
    setDraft((d) => {
      const addOns = { ...d.addOns, [id]: { ...(d.addOns?.[id] ?? { enabled: false }), ...next } };
      // The DDoS add-ons exclude each other.
      if (next.enabled && id === 'ddos-ip-protection' && addOns['ddos-network-protection'])
        addOns['ddos-network-protection'] = { ...addOns['ddos-network-protection'], enabled: false };
      if (next.enabled && id === 'ddos-network-protection' && addOns['ddos-ip-protection'])
        addOns['ddos-ip-protection'] = { ...addOns['ddos-ip-protection'], enabled: false };
      return { ...d, addOns };
    });
  const setTier = (tier: string) =>
    setDraft((d) => {
      const addOns = { ...d.addOns };
      for (const a of kind.addOns)
        if (a.requiresTier && addOns[a.id]) {
          // An add-on included in the tier (no own cost) follows it; a paid one stays off until chosen.
          const included = !a.cost && !a.commitment && a.default === false && a.id === 'private-origins';
          addOns[a.id] = {
            ...addOns[a.id],
            enabled: a.requiresTier === tier ? included || addOns[a.id].enabled : false,
          };
        }
      if (tier !== 'premium' && addOns.waf)
        addOns.waf = { ...addOns.waf, managedRules: null, botProtection: null };
      return { ...d, tier, addOns };
    });
  const setDomain = (i: number, next: Partial<NonNullable<EntryPointSettings['domains']>[number]>) =>
    setDraft((d) => ({ ...d, domains: (d.domains ?? []).map((x, j) => (j === i ? { ...x, ...next } : x)) }));
  const setRoute = (i: number, next: Partial<NonNullable<EntryPointSettings['routes']>[number]>) =>
    setDraft((d) => ({ ...d, routes: (d.routes ?? []).map((x, j) => (j === i ? { ...x, ...next } : x)) }));

  const certificates = kind.certificates ?? ['managed', 'key-vault'];
  const domainNames = (draft.domains ?? []).map((d) => d.hostname.trim()).filter(Boolean);

  const costs = (dry?.data?.impact?.costs ?? []).filter((c) => c.change === 'add');
  const costLine =
    dry?.state === 'error'
      ? null
      : !dry?.data
        ? e.costsPending
        : costs.length
          ? e.costLine(
              costs.map((c) => e.costItem(c.item, c.currency, c.monthly)).join(', '),
              asOfWords(costs[0].asOf),
            )
          : e.noFixedCost;

  // ---------------------------------------------------------------- render

  const field = (path: string) => `${ids}${path.replace(/\//g, '-')}`;
  const describe = (path: string, extra?: string) => {
    const has = messages(path, true).length > 0;
    return [has ? `${field(path)}-err` : null, extra].filter(Boolean).join(' ') || undefined;
  };

  return (
    <Dialog
      title={editing ? e.editTitle(editing.id) : e.newTitle(kind.label)}
      onClose={onClose}
      wide
      className="dialog-sheet net-ep-form"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <p className="mr-auto text-[12.5px] text-fg-muted" aria-live="polite" data-testid="ep-impact">
            {dry?.state === 'error' ? (
              <>
                {e.impactUnknown}{' '}
                <button type="button" className="link" onClick={() => setRetry((x) => x + 1)}>
                  <RefreshCw aria-hidden className="mr-1 inline size-3.5" />
                  {strings.common.retry}
                </button>
              </>
            ) : dry?.data?.impact ? (
              <span className={dry.state === 'loading' ? 'italic' : undefined}>
                {dry.data.impact.summary}
              </span>
            ) : (
              <span className="italic">{e.checking}</span>
            )}
            {errors.length > 0 && (
              <span className="block text-[var(--danger)]">{e.fixErrors(errors.length)}</span>
            )}
          </p>
          <button type="button" className="btn" onClick={onClose}>
            {strings.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            aria-disabled={errors.length > 0 || busy ? true : undefined}
            onClick={() => void save()}
          >
            {busy ? e.saving : e.save}
          </button>
        </div>
      }
    >
      {message && <p className="callout callout-warning mb-3">{message}</p>}
      {elsewhere.length > 0 && (
        <ul className="callout callout-danger mb-3">
          {elsewhere.map((d, i) => (
            <li key={i}>{d.message}</li>
          ))}
        </ul>
      )}
      <Section title={e.name}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor={field('/id')}>
              {e.id}
            </label>
            <input
              id={field('/id')}
              className="input w-full font-mono"
              value={draft.id}
              readOnly={!!editing}
              aria-invalid={messages('/id', true).length > 0 || undefined}
              aria-describedby={describe('/id', `${field('/id')}-help`)}
              onChange={(event) => setDraft((d) => ({ ...d, id: event.target.value }))}
            />
            <p id={`${field('/id')}-help`} className="help">
              {e.idHelp}
            </p>
            <FieldMessages id={`${field('/id')}-err`} messages={messages('/id', true)} />
          </div>
          <div>
            <label className="label" htmlFor={field('/label')}>
              {e.label}
            </label>
            <input
              id={field('/label')}
              className="input w-full"
              value={draft.label ?? ''}
              onChange={(event) => setDraft((d) => ({ ...d, label: event.target.value }))}
            />
          </div>
        </div>
      </Section>

      <Section title={e.tier}>
        <div role="radiogroup" aria-label={e.tier} className="net-ep-tiers">
          {kind.tiers.map((t) => (
            <label key={t.id} className={`net-ep-tier ${draft.tier === t.id ? 'is-checked' : ''}`}>
              <input
                type="radio"
                name={`${ids}-tier`}
                checked={draft.tier === t.id}
                onChange={() => setTier(t.id)}
              />
              <span>
                <span className="block font-semibold">
                  {t.cost ? e.tierCost(t.label, t.cost.currency, t.cost.monthly) : t.label}
                </span>
                {t.id === 'premium' && (
                  <span className="block text-[12px] text-fg-muted">{e.premiumExtras}</span>
                )}
              </span>
            </label>
          ))}
        </div>
        {kind.scope === 'regional' && (
          <div className="mt-3">
            <label className="label" htmlFor={field('/region')}>
              {e.region}
            </label>
            <select
              id={field('/region')}
              className="input"
              value={draft.region ?? ''}
              aria-invalid={messages('/region').length > 0 || undefined}
              aria-describedby={describe('/region', `${field('/region')}-help`)}
              onChange={(event) => setDraft((d) => ({ ...d, region: event.target.value }))}
            >
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <p id={`${field('/region')}-help`} className="help">
              {e.regionHelp}
            </p>
            <FieldMessages
              id={`${field('/region')}-err`}
              messages={at('/region').map((d) =>
                d.code === 'AZURE_APP_GATEWAY_HUB_TOO_SMALL'
                  ? e.codes.AZURE_APP_GATEWAY_HUB_TOO_SMALL
                  : d.message,
              )}
            />
          </div>
        )}
      </Section>

      {kind.addOns.length > 0 && (
        <Section title={e.addOns}>
          <ul className="space-y-2">
            {kind.addOns.map((a) => {
              const state = draft.addOns?.[a.id] ?? { enabled: false };
              const tierOk = !a.requiresTier || a.requiresTier === draft.tier;
              const needs = tierOk
                ? null
                : e.needsTier(kind.tiers.find((t) => t.id === a.requiresTier)?.label ?? a.requiresTier ?? '');
              const id = field(`/addOns/${a.id}`);
              const sentence = a.byTier?.[draft.tier ?? ''] ?? null;
              const cost = a.cost
                ? `${e.addOnCost(a.cost.currency, a.cost.monthly)}${a.id === 'ddos-ip-protection' ? ` ${e.perPublicIp}` : ''}`
                : null;
              const waf = a.id === 'waf' && state.enabled;
              const extra = [needs, cost].filter(Boolean).join(' · ');
              const described = !!(sentence || extra);
              return (
                <li key={a.id} className="net-ep-addon" data-testid={`ep-addon-${a.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id={id}
                      type="checkbox"
                      role="switch"
                      checked={state.enabled && tierOk}
                      aria-disabled={needs ? true : undefined}
                      aria-describedby={described ? `${id}-desc` : undefined}
                      onChange={(event) => {
                        if (needs) return;
                        setAddOn(a.id, { enabled: event.target.checked });
                      }}
                    />
                    <label htmlFor={id} className="font-medium">
                      {a.label}
                    </label>
                    {cost && <span className="badge tone-neutral">{cost}</span>}
                    {needs && <span className="badge tone-neutral">{needs}</span>}
                  </div>
                  {described && (
                    <p id={`${id}-desc`} className={sentence ? 'help mt-0.5' : 'sr-only'}>
                      {sentence}
                      {extra && <span className="sr-only">{sentence ? ` · ${extra}` : extra}</span>}
                    </p>
                  )}
                  {waf && (
                    <div className="net-ep-waf">
                      <div role="radiogroup" aria-label={e.wafMode} className="flex flex-wrap gap-3">
                        {(['prevention', 'detection'] as const).map((mode) => (
                          <label key={mode} className="flex items-center gap-1.5 text-sm">
                            <input
                              type="radio"
                              name={`${ids}-waf-mode`}
                              checked={(state.mode ?? 'prevention') === mode}
                              onChange={() => setAddOn('waf', { mode })}
                            />
                            {mode === 'prevention' ? e.prevention : e.detection}
                          </label>
                        ))}
                      </div>
                      {(state.mode ?? 'prevention') === 'detection' && (
                        <p className="help">{e.detectionHelp}</p>
                      )}
                      {draft.tier === 'premium' ? (
                        <div className="flex flex-wrap gap-4">
                          {(['managedRules', 'botProtection'] as const).map((key) => (
                            <label key={key} className="flex items-center gap-1.5 text-sm">
                              <input
                                type="checkbox"
                                // Both default to true (spec 18 5.1), so an unset value reads as on.
                                checked={state[key] ?? true}
                                onChange={(event) => setAddOn('waf', { [key]: event.target.checked })}
                              />
                              {key === 'managedRules' ? e.managedRules : e.botProtection}
                            </label>
                          ))}
                        </div>
                      ) : kind.tiers.some((t) => t.id === 'premium') ? (
                        <p className="help">{e.managedNeedsPremium}</p>
                      ) : null}
                      <div>
                        <label className="label" htmlFor={field('/addOns/waf/rateLimitPerMinute')}>
                          {e.rateLimit}
                        </label>
                        <input
                          id={field('/addOns/waf/rateLimitPerMinute')}
                          type="number"
                          min={0}
                          max={100000}
                          className="input w-40"
                          value={state.rateLimitPerMinute ?? ''}
                          aria-describedby={describe(
                            '/addOns/waf/rateLimitPerMinute',
                            `${field('/addOns/waf/rateLimitPerMinute')}-help`,
                          )}
                          onChange={(event) =>
                            setAddOn('waf', {
                              rateLimitPerMinute:
                                event.target.value === '' ? null : Number(event.target.value),
                            })
                          }
                        />
                        <p id={`${field('/addOns/waf/rateLimitPerMinute')}-help`} className="help">
                          {e.rateLimitHelp}
                        </p>
                        <FieldMessages
                          id={`${field('/addOns/waf/rateLimitPerMinute')}-err`}
                          messages={messages('/addOns/waf/rateLimitPerMinute', true)}
                        />
                      </div>
                    </div>
                  )}
                  <FieldMessages messages={messages(`/addOns/${a.id}`, true)} />
                </li>
              );
            })}
          </ul>
          {kind.addOns.some((a) => a.id === 'ddos-ip-protection') && (
            <p className="help">{e.ddosExclusive}</p>
          )}
        </Section>
      )}

      <Section title={e.domains} help={e.domainsHelp}>
        {kind.kind === 'azure-app-gateway' && <p className="help mt-0 mb-2">{e.keyVaultOnly}</p>}
        <FieldMessages messages={messages('/domains', true)} />
        <ul className="space-y-2">
          {(draft.domains ?? []).map((d, i) => (
            <li key={i} className="net-ep-row" data-testid={`ep-form-domain-${i}`}>
              <div className="min-w-0 flex-1">
                <label className="sr-only" htmlFor={field(`/domains/${i}/hostname`)}>
                  {e.domainHost(i + 1)}
                </label>
                <input
                  id={field(`/domains/${i}/hostname`)}
                  className="input w-full font-mono"
                  placeholder="shop.example.com"
                  value={d.hostname}
                  aria-invalid={messages(`/domains/${i}/hostname`, true).length > 0 || undefined}
                  aria-describedby={describe(`/domains/${i}/hostname`)}
                  onChange={(event) => setDomain(i, { hostname: event.target.value })}
                />
                <FieldMessages
                  id={`${field(`/domains/${i}/hostname`)}-err`}
                  messages={messages(`/domains/${i}/hostname`, true)}
                />
              </div>
              <div>
                <select
                  className="input"
                  aria-label={e.domainCertificate(i + 1)}
                  value={d.certificate.kind}
                  onChange={(event) =>
                    setDomain(i, {
                      certificate: {
                        kind: event.target.value as 'managed' | 'key-vault',
                        secretId: d.certificate.secretId,
                      },
                    })
                  }
                >
                  {certificates.map((c) => (
                    <option key={c} value={c}>
                      {c === 'managed' ? e.managed : e.keyVault}
                    </option>
                  ))}
                </select>
                <FieldMessages messages={messages(`/domains/${i}/certificate/kind`, true)} />
              </div>
              {d.certificate.kind === 'key-vault' && (
                <div className="min-w-0 flex-1">
                  <input
                    id={field(`/domains/${i}/certificate/secretId`)}
                    className="input w-full font-mono"
                    placeholder="https://<vault>.vault.azure.net/secrets/<name>"
                    aria-label={e.secretId(i + 1)}
                    value={d.certificate.secretId ?? ''}
                    aria-describedby={describe(`/domains/${i}/certificate/secretId`)}
                    onChange={(event) =>
                      setDomain(i, { certificate: { kind: 'key-vault', secretId: event.target.value } })
                    }
                  />
                  <FieldMessages
                    id={`${field(`/domains/${i}/certificate/secretId`)}-err`}
                    messages={messages(`/domains/${i}/certificate/secretId`, true)}
                  />
                </div>
              )}
              <IconButton
                label={e.removeDomain(d.hostname)}
                onClick={() =>
                  setDraft((x) => ({ ...x, domains: (x.domains ?? []).filter((_, j) => j !== i) }))
                }
              >
                <Trash2 aria-hidden className="size-4" />
              </IconButton>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="btn btn-sm mt-2"
          onClick={() =>
            setDraft((x) => ({
              ...x,
              domains: [
                ...(x.domains ?? []),
                { hostname: '', certificate: { kind: certificates[0] ?? 'managed' } },
              ],
            }))
          }
        >
          <Plus aria-hidden className="size-4" />
          {e.addDomain}
        </button>
      </Section>

      <Section title={e.routes} help={e.routesHelp}>
        {!origins.loading && origins.options.length === 0 && <p className="help">{e.noOrigins(n.name)}</p>}
        <ol className="space-y-3">
          {(draft.routes ?? []).map((r, i) => {
            const option = origins.options.find(
              (o) => o.deploymentId === r.origin.deploymentId && o.resourceId === r.origin.resourceId,
            );
            const platform = option?.platform ?? null;
            const support = platform ? kind.origins.find((o) => o.platform === platform) : undefined;
            const offered = platform ? offeredModes(draft, kind, platform) : [];
            const preselected = platform ? preselectedMode(draft, kind, platform) : null;
            const chosen: OriginAccessMode | null =
              r.originAccess && r.originAccess !== 'auto' ? r.originAccess : preselected;
            const modes = [
              ...ACCESS_ORDER.filter((m) => (support?.access as string[] | undefined)?.includes(m)),
              'none' as const,
            ];
            const pushShown =
              r.allowPlatformPush !== undefined ||
              hasCode(`/routes/${i}`, 'ORIGIN_PLATFORM_PUSH_NEEDS_CHOICE', 'ORIGIN_LOCKDOWN_ALLOWS_PLATFORM');
            const grouped = new Map<string, OriginOption[]>();
            for (const o of origins.options)
              grouped.set(o.deploymentName, [...(grouped.get(o.deploymentName) ?? []), o]);
            const originErrors = at(`/routes/${i}/origin`).map((d) =>
              d.code === 'ROUTE_ORIGIN_NOT_ATTACHED'
                ? e.codes.ROUTE_ORIGIN_NOT_ATTACHED(option?.deploymentName ?? d.message, n.name)
                : d.code === 'ROUTE_ORIGIN_UNKNOWN'
                  ? e.codes.ROUTE_ORIGIN_UNKNOWN
                  : d.message,
            );
            return (
              <li key={i} className="net-ep-route" data-testid={`ep-form-route-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-[13px] font-semibold">{e.route(i + 1)}</h4>
                  <IconButton
                    label={e.removeRoute(r.id)}
                    onClick={() => {
                      setDraft((x) => ({ ...x, routes: (x.routes ?? []).filter((_, j) => j !== i) }));
                      setPathsText((p) => p.filter((_, j) => j !== i));
                    }}
                  >
                    <Trash2 aria-hidden className="size-4" />
                  </IconButton>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="label" htmlFor={field(`/routes/${i}/id`)}>
                      {e.routeId(i + 1)}
                    </label>
                    <input
                      id={field(`/routes/${i}/id`)}
                      className="input w-full font-mono"
                      value={r.id}
                      aria-describedby={describe(`/routes/${i}/id`)}
                      onChange={(event) => setRoute(i, { id: event.target.value })}
                    />
                    <FieldMessages
                      id={`${field(`/routes/${i}/id`)}-err`}
                      messages={messages(`/routes/${i}/id`, true)}
                    />
                  </div>
                  <fieldset>
                    <legend className="label">{e.routeHosts(i + 1)}</legend>
                    {domainNames.length === 0 ? (
                      <p className="help mt-0">{kind.defaultHost ? e.edgeOwnHost : e.allDomains}</p>
                    ) : (
                      <>
                        <p className="help mt-0">{(r.hostnames ?? []).length === 0 ? e.allDomains : null}</p>
                        {domainNames.map((h) => (
                          <label key={h} className="flex items-center gap-1.5 text-sm">
                            <input
                              type="checkbox"
                              checked={(r.hostnames ?? []).includes(h)}
                              onChange={(event) =>
                                setRoute(i, {
                                  hostnames: event.target.checked
                                    ? [...(r.hostnames ?? []), h]
                                    : (r.hostnames ?? []).filter((x) => x !== h),
                                })
                              }
                            />
                            <code className="font-mono text-[12px]">{h}</code>
                          </label>
                        ))}
                      </>
                    )}
                  </fieldset>
                  <div>
                    <label className="label" htmlFor={field(`/routes/${i}/paths`)}>
                      {e.routePaths(i + 1)}
                    </label>
                    <input
                      id={field(`/routes/${i}/paths`)}
                      className="input w-full font-mono"
                      value={pathsText[i] ?? '/*'}
                      aria-describedby={describe(`/routes/${i}/paths`, `${field(`/routes/${i}/paths`)}-help`)}
                      onChange={(event) =>
                        setPathsText((p) => {
                          const next = [...p];
                          next[i] = event.target.value;
                          return next;
                        })
                      }
                    />
                    <p id={`${field(`/routes/${i}/paths`)}-help`} className="help">
                      {e.pathsHelp}
                    </p>
                    <FieldMessages
                      id={`${field(`/routes/${i}/paths`)}-err`}
                      messages={messages(`/routes/${i}/paths`, true)}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="label" htmlFor={field(`/routes/${i}/origin`)}>
                    {e.routeOrigin(i + 1)}
                  </label>
                  <select
                    id={field(`/routes/${i}/origin`)}
                    className="input w-full sm:w-auto"
                    value={r.origin.deploymentId ? `${r.origin.deploymentId}/${r.origin.resourceId}` : ''}
                    aria-invalid={originErrors.length > 0 || undefined}
                    aria-describedby={originErrors.length ? `${field(`/routes/${i}/origin`)}-err` : undefined}
                    onChange={(event) => {
                      const [deploymentId, resourceId] = event.target.value.split('/');
                      setRoute(i, {
                        origin: { deploymentId: deploymentId ?? '', resourceId: resourceId ?? '' },
                        originAccess: 'auto',
                        allowPlatformPush: undefined,
                      });
                    }}
                  >
                    <option value="">{e.chooseOrigin}</option>
                    {[...grouped].map(([deployment, apps]) => (
                      <optgroup key={deployment} label={deployment}>
                        {apps.map((o) => (
                          <option
                            key={`${o.deploymentId}/${o.resourceId}`}
                            value={`${o.deploymentId}/${o.resourceId}`}
                            disabled={!!o.unavailable}
                          >
                            {`${o.deploymentName} / ${o.resourceId} · ${o.label}${o.unavailable ? ` (${o.unavailable})` : ''}`}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {kind.scope === 'regional' && draft.region && (
                    <p className="help">{e.inRegion(draft.region)}</p>
                  )}
                  <FieldMessages id={`${field(`/routes/${i}/origin`)}-err`} messages={originErrors} />
                </div>
                {platform && (
                  <fieldset className="mt-2" data-testid={`ep-form-access-${i}`}>
                    <legend className="label">{e.routeAccess(i + 1)}</legend>
                    {support?.default === null && <p className="help mt-0">{e.mustChoose}</p>}
                    <div role="radiogroup" aria-label={e.routeAccess(i + 1)} className="space-y-1">
                      {modes.map((m) => {
                        const available = m === 'none' || offered.includes(m);
                        const descId = `${field(`/routes/${i}/originAccess`)}-${m}`;
                        return (
                          <label key={m} className={`net-ep-access ${available ? '' : 'is-disabled'}`}>
                            <input
                              type="radio"
                              name={`${ids}-access-${i}`}
                              checked={chosen === m}
                              aria-disabled={available ? undefined : true}
                              aria-describedby={descId}
                              onChange={() => {
                                if (!available) return;
                                setRoute(i, { originAccess: m === preselected ? 'auto' : m });
                              }}
                            />
                            <span>
                              <span className="font-medium">{e.accessLabel[m]}</span>{' '}
                              <span className="text-[12px] text-fg-muted">
                                ({e.strength[ACCESS_STRENGTH[m]]})
                              </span>
                              <span id={descId} className="block text-[12px] text-fg-muted">
                                {available
                                  ? e.accessSentence[m]
                                  : `${e.accessSentence[m]} ${e.needsTier('Premium with private origins')}.`}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <FieldMessages messages={messages(`/routes/${i}/originAccess`, true)} />
                  </fieldset>
                )}
                {pushShown && (
                  <div className="mt-2">
                    <label className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={!!r.allowPlatformPush}
                        aria-describedby={`${field(`/routes/${i}/allowPlatformPush`)}-help`}
                        onChange={(event) => setRoute(i, { allowPlatformPush: event.target.checked })}
                      />
                      {e.push}
                    </label>
                    <p id={`${field(`/routes/${i}/allowPlatformPush`)}-help`} className="help">
                      {e.pushHelp(r.origin.resourceId)}
                    </p>
                    <FieldMessages
                      messages={at(`/routes/${i}/allowPlatformPush`, true)
                        .filter((d) => d.severity === 'error')
                        .map((d) => d.message)}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <button
          type="button"
          className="btn btn-sm mt-2"
          onClick={() => {
            const routes = draft.routes ?? [];
            setDraft((x) => ({
              ...x,
              routes: [
                ...(x.routes ?? []),
                {
                  id: routes.length ? `route-${routes.length + 1}` : 'web',
                  hostnames: [],
                  paths: ['/*'],
                  origin: { deploymentId: '', resourceId: '' },
                  originAccess: 'auto',
                },
              ],
            }));
            setPathsText((p) => [...p, '/*']);
          }}
        >
          <Plus aria-hidden className="size-4" />
          {e.addRoute}
        </button>
      </Section>

      <p className="net-ep-costs" data-testid="ep-costs">
        <span className="font-semibold">{e.costs}:</span> {costLine ?? e.impactUnknown}
      </p>
    </Dialog>
  );
}
